// components/Header.js
import React from 'react';
import { AppBar, Toolbar, Typography, Menu, MenuItem, Radio, RadioGroup, FormControlLabel, FormControl } from '@mui/material';
import { FaBars } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { ThemeContext } from '../utils/ThemeContext'; // <-- Added
import { apiCall } from '../utils/api';

const formatWalletPoints = (balance) => {
    if (!Number.isFinite(balance)) {
        return null;
    }

    const formatWithSuffix = (value, suffix) => {
        const rounded = value >= 10 ? Math.round(value).toString() : value.toFixed(1).replace(/\.0$/, '');
        return `${rounded}${suffix}`;
    };

    if (balance >= 1_000_000) {
        const value = balance / 1_000_000;
        return formatWithSuffix(value, 'M');
    }

    if (balance >= 1_000) {
        const value = balance / 1_000;
        return formatWithSuffix(value, 'K');
    }

    return Math.round(balance).toString();
};

const Header = ({ userName, isSignedIn, onSignInClick, isSigningIn, canTriggerSignIn = true, onMenuToggle = () => {}, isSidebarExpanded = false }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const navigate = useNavigate();
    const { authState, signOut: signOutUser, refreshAccessToken } = useAuth();
    const { theme, toggleTheme } = React.useContext(ThemeContext); // <-- Use theme
    const [walletBalance, setWalletBalance] = React.useState(null);
    const [isWalletLoading, setIsWalletLoading] = React.useState(false);

    const fetchWalletBalance = React.useCallback(async () => {
        let active = true;

        if (!isSignedIn || !authState?.accessToken || !authState?.userId) {
            setWalletBalance(null);
            setIsWalletLoading(false);
            return () => {
                active = false;
            };
        }

        setIsWalletLoading(true);

        const loadBalance = async (token) => {
            try {
                const response = await apiCall('/wallet/balance?product_id=2', 'GET', null, true, null, token);
                if (!active) return;

                const parsed = typeof response?.balance === 'number' ? response.balance : Number(response?.balance);
                setWalletBalance(Number.isFinite(parsed) ? parsed : 0);
            } catch (error) {
                if (!active) return;

                if (error?.status === 401 && typeof refreshAccessToken === 'function') {
                    try {
                        const newToken = await refreshAccessToken();
                        if (!newToken) {
                            setWalletBalance(null);
                            return;
                        }
                        const retryResponse = await apiCall('/wallet/balance?product_id=2', 'GET', null, true, null, newToken);
                        if (!active) return;
                        const parsedRetry = typeof retryResponse?.balance === 'number' ? retryResponse.balance : Number(retryResponse?.balance);
                        setWalletBalance(Number.isFinite(parsedRetry) ? parsedRetry : 0);
                    } catch (retryError) {
                        console.error('[Header] Failed to refresh token while fetching wallet balance:', retryError);
                        setWalletBalance(null);
                    }
                    return;
                }

                if (error?.status === 404) {
                    setWalletBalance(0);
                    return;
                }

                console.error('[Header] Failed to fetch wallet balance:', error);
                setWalletBalance(null);
            }
        };

        await loadBalance(authState.accessToken);

        if (active) {
            setIsWalletLoading(false);
        }

        return () => {
            active = false;
        };
    }, [authState?.accessToken, authState?.userId, isSignedIn, refreshAccessToken]);

    React.useEffect(() => {
        let cancel;
        fetchWalletBalance().then((cleanup) => {
            cancel = cleanup;
        });

        return () => {
            if (typeof cancel === 'function') {
                cancel();
            }
        };
    }, [fetchWalletBalance]);

    React.useEffect(() => {
        if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
            return undefined;
        }

        const handleWalletUpdate = (event) => {
            const incomingBalance = event?.detail?.balance;

            if (incomingBalance === undefined) {
                return;
            }

            if (incomingBalance === null) {
                setWalletBalance(null);
                setIsWalletLoading(false);
                return;
            }

            const nextBalance = typeof incomingBalance === 'number'
                ? incomingBalance
                : Number(incomingBalance);

            if (Number.isFinite(nextBalance)) {
                setWalletBalance(nextBalance);
            } else {
                setWalletBalance(null);
            }

            setIsWalletLoading(false);
        };

        window.addEventListener('walletBalanceUpdated', handleWalletUpdate);

        return () => {
            window.removeEventListener('walletBalanceUpdated', handleWalletUpdate);
        };
    }, []);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleProfileClick = () => {
        navigate('/profile');
        handleClose();
    };

    const handlePrivacyPolicy = () => {
        navigate('/privacy-policy');
        handleClose();
    };

    const handleWalletNavigation = () => {
        navigate('/upgrade');
        handleClose();
    };

    const handleSignOut = async () => {
        await signOutUser();
        navigate('/');
        handleClose();
    };

    const handleThemeChange = (event) => {
        toggleTheme(event.target.value);
    };

    const firstInitial = userName ? userName.charAt(0).toUpperCase() : 'U';
    const formattedWalletBalance = walletBalance !== null ? formatWalletPoints(walletBalance) : null;
    const roundedWalletBalance = Number.isFinite(walletBalance) ? Math.round(walletBalance) : null;
    const walletPointsDisplay = formattedWalletBalance ?? (isWalletLoading ? '...' : null);
    const profileCircleLabel = formattedWalletBalance !== null && roundedWalletBalance !== null
        ? `${roundedWalletBalance.toLocaleString()} wallet points`
        : isWalletLoading
            ? 'Loading wallet points'
            : `${userName || 'User'} menu`;
    const walletPointsMenuText = walletBalance !== null
        ? `${walletPointsDisplay} pts`
        : isWalletLoading
            ? 'Loading points...'
            : 'Points unavailable';

    return (
        <AppBar
            position="sticky"
            className="app-bar"
            sx={{
                top: 0,
                zIndex: (theme) => theme.zIndex.drawer + 2
            }}
        >
            <Toolbar sx={{ pl: isSignedIn ? { xs: 0.5, sm: 0 } : undefined }}>
                {isSignedIn && (
                    <button
                        type="button"
                        className="menu-toggle"
                        onClick={onMenuToggle}
                        aria-label={isSidebarExpanded ? 'Close navigation menu' : 'Open navigation menu'}
                        aria-pressed={isSidebarExpanded}
                        aria-expanded={isSidebarExpanded}
                        title={isSidebarExpanded ? 'Close menu' : 'Open menu'}
                    >
                        <FaBars />
                    </button>
                )}
                <Typography
                    variant="h6"
                    component="a"
                    href="https://quizacer.com"
                    sx={{
                        flexGrow: 1,
                        ml: isSignedIn ? 1 : 0,
                        fontWeight: 'bold',
                        fontSize: { xs: '0.875rem', sm: '1.25rem' },
                        letterSpacing: '0.08em',
                        color: 'inherit',
                        textDecoration: 'none',
                        cursor: 'pointer'
                    }}
                >
                    QuizAcer
                </Typography>
                {!isSignedIn && (
                    <button
                        className="sign-in-button button google-sign-in-button header-sign-in-button"
                        onClick={onSignInClick}
                        disabled={isSigningIn || !onSignInClick || !canTriggerSignIn}
                    >
                        <span className="google-icon" aria-hidden="true">
                            <FcGoogle />
                        </span>
                        <span>{isSigningIn ? 'Signing in...' : 'Sign In with Google'}</span>
                    </button>
                )}
                {isSignedIn && (
                    <div className="profile-menu-container">
                        <div className="profile-icon" onClick={handleMenu}>
                            <div className="profile-circle" title={profileCircleLabel} aria-label={profileCircleLabel}>
                                <span className="profile-initial" aria-hidden="true">{firstInitial}</span>
                                {walletPointsDisplay && (
                                    <span className="profile-wallet-points" aria-hidden="true">{walletPointsDisplay}</span>
                                )}
                            </div>
                        </div>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <div className="menu-profile-summary" role="presentation">
                                <span className="menu-profile-initial" aria-hidden="true">{firstInitial}</span>
                                <div className="menu-profile-info">
                                    <span className="menu-profile-name">{userName || 'User'}</span>
                                    <span className="menu-profile-points">{walletPointsMenuText}</span>
                                </div>
                            </div>
                            <MenuItem onClick={handleProfileClick}>Profile</MenuItem>
                            <MenuItem>
                                <FormControl component="fieldset">
                                    <RadioGroup row value={theme} onChange={handleThemeChange}>
                                        <FormControlLabel value="light" control={<Radio size="small" />} label="Light" />
                                        <FormControlLabel value="dark" control={<Radio size="small" />} label="Dark" />
                                    </RadioGroup>
                                </FormControl>
                            </MenuItem>

                            <MenuItem onClick={handlePrivacyPolicy}>Privacy Policy</MenuItem>
                            <MenuItem onClick={handleWalletNavigation}>Wallet</MenuItem>
                            <MenuItem onClick={handleSignOut}>Logout</MenuItem>
                        </Menu>
                    </div>
                )}
            </Toolbar>
        </AppBar>
    );
};

export default Header;